import type { Role } from '@time-tracking/shared';
import { Fragment, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import { ChevronDownIcon } from '../../components/icons/ChevronDownIcon.js';
import { InfoIcon } from '../../components/icons/InfoIcon.js';
import type { FaqAnswerBlock, FaqEntry, FaqGroup, FaqSpan } from './faq-content.js';
import { FAQ_ENTRIES, faqGroupsFor } from './faq-content.js';

/**
 * The tier shown when `/v1/me` fails and the viewer's real role is unknowable.
 *
 * Contributor is the floor every role nests on, so it is the only tier that is
 * true for everyone. Named rather than inlined per CLAUDE.md's no-magic-strings
 * rule.
 */
const FAQ_FALLBACK_ROLE: Role = 'contributor';

/**
 * Which of the four Quick-answers renderings applies. Resolved once by
 * `HelpPage` and passed in, because two consumers depend on the same verdict:
 * the accordion body and the section header's right-hand note, which is
 * suppressed unless the role is known AND something is published. Deriving it
 * twice is how those two end up disagreeing — a note reading "Written for
 * contributors" over an empty-state callout.
 *
 * `pending` and `empty` are deliberately separate members. Collapsing them is
 * the bug this shape exists to prevent: `useMe` is unresolved on every first
 * paint, and an empty-state callout flashed there tells the viewer their help
 * page is blank when it is merely still loading.
 */
export type FaqSectionState =
  /** `/v1/me` has not resolved and has not failed. Skeleton, never the callout. */
  | { readonly kind: 'pending' }
  /** Nothing to show. `reason` picks the callout's title. */
  | { readonly kind: 'empty'; readonly reason: 'no-entries' | 'no-entries-for-role' }
  /** Role known, entries resolved. The only state that earns the scope note. */
  | { readonly kind: 'answers'; readonly role: Role; readonly groups: readonly FaqGroup[] }
  /** `/v1/me` failed: the contributor tier plus a note that the role is missing. */
  | { readonly kind: 'degraded'; readonly groups: readonly FaqGroup[] };

interface ResolveFaqSectionInput {
  /** `meQuery.data?.role` — undefined while pending and when `/v1/me` fails. */
  readonly role: Role | undefined;
  /** `meQuery.isError`. Separates "still waiting" from "will never arrive". */
  readonly roleUnavailable: boolean;
  /** Injectable for tests; defaults to the published copy. */
  readonly entries?: readonly FaqEntry[];
}

/**
 * Decide what the Quick answers section renders.
 *
 * The error branch is load-bearing, not defensive. `ProtectedRoute` renders
 * `null` while `useMe` is loading (`router.tsx:22`) — so it does await the query,
 * contrary to what this comment claimed until the whole-page a11y review measured
 * it — but once the query SETTLES to an error it proceeds and mounts this page.
 * From that point nothing retries on its own, so a page that showed a skeleton
 * for the error state would spin forever. A federated login
 * with no pre-provisioned row is exactly that case (see the `useMe` retry
 * comment in `api/hooks.ts`), and Help & FAQ is where that user goes looking for
 * an explanation. A skeleton there would spin forever; the contributor tier is
 * true for every role, so it is what they get.
 */
export function resolveFaqSection({
  role,
  roleUnavailable,
  entries = FAQ_ENTRIES,
}: ResolveFaqSectionInput): FaqSectionState {
  if (role === undefined && !roleUnavailable) return { kind: 'pending' };

  const groups = faqGroupsFor(role ?? FAQ_FALLBACK_ROLE, entries);
  if (groups.length === 0) {
    // "Nothing published anywhere" and "nothing published for this role" are
    // different facts, and the entry list the caller passed is what tells them
    // apart (see `faqGroupsFor`'s contract). The second is reachable whenever
    // every entry authored at this role's tiers is removed while higher tiers
    // keep theirs.
    return { kind: 'empty', reason: entries.length === 0 ? 'no-entries' : 'no-entries-for-role' };
  }

  return role === undefined ? { kind: 'degraded', groups } : { kind: 'answers', role, groups };
}

/**
 * The Quick answers body: a multi-open disclosure list, its tier label bars, the
 * empty-state callout, a loading skeleton, and the degraded-role fallback.
 *
 * PROTOTYPE OF RECORD: `design_handoff_help_faq/` — `02-contributor-faq.png`,
 * `06`/`07` for grouping, `08` for the empty state. This component owns all
 * markup for `faq-content.ts`'s answer blocks; that module stays copy-only.
 *
 * `<details>`/`<summary>` was rejected as the primitive: it cannot unmount its
 * panel, so collapsed answers would stay in the accessibility tree. See the
 * plan's decision log.
 */
/**
 * Restores focus when a post-mount state change destroys the subtree holding it.
 *
 * The branches below return different element TYPES at the same position (a fragment for
 * `degraded`, a bare `FaqList` for `answers`), and `answers` additionally keys its list on
 * the role so the real tiers open from the top. Both are deliberate — but React responds by
 * destroying the outgoing subtree, and a keyboard user who had focus on a trigger inside it
 * loses focus to `<body>`, so their next Tab restarts at the top of the document (WCAG
 * 2.4.3).
 *
 * This is reachable rather than theoretical: `App.tsx` sets `staleTime: 30_000` and leaves
 * `refetchOnWindowFocus` at TanStack Query's default of `true`, and this page's two primary
 * calls to action deliberately open new tabs — so following one and coming back is exactly
 * the window-focus event that refetches `useMe` and can flip `degraded` to `answers`
 * underneath someone.
 *
 * Found by the whole-page a11y review. No single-state test could see it: every existing
 * test renders one fixed state, and the defect only exists in the transition between two
 * individually-valid renders. axe cannot see it either — it samples one snapshot.
 */
function useFocusAfterStateChange(state: FaqSectionState) {
  const firstRender = useRef(true);
  const focusWasInside = useRef(false);
  const anchorKey = state.kind === 'answers' ? state.role : state.kind;

  /**
   * Tracks whether focus is currently inside the accordion.
   *
   * A layout effect runs AFTER the DOM has changed, by which point the outgoing subtree and
   * whatever was focused in it are already gone — so the fact has to be recorded while it is
   * still true. `focusin` bubbles (unlike `focus`), so one document listener covers every
   * trigger without per-element wiring.
   */
  useEffect(() => {
    const onFocusIn = (event: FocusEvent) => {
      const target = event.target;
      focusWasInside.current = target instanceof Element && target.closest('.help-faq') !== null;
    };
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, []);

  useLayoutEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    // BOTH CONDITIONS ARE REQUIRED, and the first one is the whole point.
    //
    // `activeElement === body` alone is not evidence that focus was ejected — it is also
    // the ordinary state when nobody has focused anything yet. The first version of this
    // checked only that, which meant a pending -> answers transition (the common path: the
    // skeleton resolves a moment after paint, with focus untouched) STOLE focus into the
    // first question unprompted. That is a worse defect than the one being fixed, and an
    // unexpected context change in its own right. Caught by Copilot on PR #74.
    //
    // So restore only when focus really was inside the accordion and is now nowhere.
    if (!focusWasInside.current) return;
    if (document.activeElement !== document.body) return;
    // Restore to the first FAQ trigger — a real button that is already a tab stop. An
    // earlier attempt focused the section's h2 with `tabIndex={-1}`, which was worse in two
    // ways: it put a tabindex on shared chrome (SectionHeader serves both sections), and it
    // polluted the page's own definition of "focusable" — the keyboard spec builds its
    // expected tab order from a selector that includes `[tabindex]`, so a heading that is
    // not tab-reachable started appearing in it. Returning the user to the top of the list
    // they were already in is also the better behaviour.
    document.querySelector<HTMLElement>('.help-faq-q')?.focus();
    focusWasInside.current = false;
  }, [anchorKey]);
}

export function FaqAccordion({ state }: { readonly state: FaqSectionState }) {
  useFocusAfterStateChange(state);

  switch (state.kind) {
    case 'pending':
      return <FaqSkeleton />;
    case 'empty':
      return (
        <FaqCallout
          title={
            state.reason === 'no-entries'
              ? 'No answers published yet'
              : 'No answers for your role yet'
          }
        />
      );
    case 'degraded':
      return (
        <>
          {/* The callout is VISUAL ONLY. The announcement is a separate persistent region
              below it — see FaqDegradedAnnouncement for why the two are split. */}
          <FaqCallout title="Showing the general answers" body={DEGRADED_BODY} />
          <FaqDegradedAnnouncement />
          {/* `key` is explicit rather than relying on this branch's root being a fragment
              while `answers` returns a bare FaqList: a later successful /v1/me must remount
              the list so the real tiers open from the top instead of inheriting this
              fallback's open rows. */}
          <FaqList key="degraded" groups={state.groups} />
        </>
      );
    case 'answers':
      // `key` on the role is what resets open state when the role changes: the
      // open set holds entry ids, and the ids a role can see change with it. A
      // remount also re-opens the first row of the new tier, which is the same
      // rule as first paint.
      return <FaqList key={state.role} groups={state.groups} />;
  }
}

/**
 * The accordion container. Groups are rendered as labelled regions only when
 * there is more than one — a contributor sees a single ungrouped list and no
 * label bars, per the handoff.
 */
function FaqList({ groups }: { readonly groups: readonly FaqGroup[] }) {
  // First row expanded on mount so the disclosure affordance is legible without
  // a click. Ids, never indices: an index means something different the moment
  // the role filter changes the visible set.
  const firstEntryId = groups[0]?.entries[0]?.id;
  const [openIds, setOpenIds] = useState<ReadonlySet<string>>(
    () => new Set(firstEntryId === undefined ? [] : [firstEntryId]),
  );

  function toggle(id: string) {
    setOpenIds((previous) => {
      const next = new Set(previous);
      // Opening one row never closes another — several answers are read side by
      // side while following a procedure.
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const singleGroup = groups.length === 1 ? groups[0] : undefined;

  return (
    <div className="help-faq">
      {singleGroup === undefined
        ? groups.map((group) => (
            <FaqTierGroup key={group.tier} group={group} openIds={openIds} onToggle={toggle} />
          ))
        : singleGroup.entries.map((entry) => (
            <FaqRow key={entry.id} entry={entry} open={openIds.has(entry.id)} onToggle={toggle} />
          ))}
    </div>
  );
}

/**
 * One tier: the uppercase label bar and its rows.
 *
 * ACCESSIBILITY DIVERGENCE FROM THE PROTOTYPE (deliberate). The prototype's
 * label bar is a plain `<div>`, which is invisible to assistive technology: a
 * sighted reader sees the tier boundary instantly, a screen-reader user hears
 * nothing between the last approver question and the first contributor one. So
 * the bar is an `h3` — the section already carries the `h2`, so h1 → h2 → h3
 * stays in order — and each tier is additionally a `role="group"` named by that
 * heading. The heading gives rotor/heading navigation straight to a tier; the
 * group gives the boundary itself, announced on entry, so a row's tier context
 * is recoverable without backing out to hunt for the label. The group's name is
 * the heading element rather than a duplicated string, so the two cannot drift.
 * WCAG 2.1 AA, 1.3.1 Info and Relationships.
 */
function FaqTierGroup({
  group,
  openIds,
  onToggle,
}: {
  readonly group: FaqGroup;
  readonly openIds: ReadonlySet<string>;
  readonly onToggle: (id: string) => void;
}) {
  const labelId = useId();

  return (
    <div className="help-faq-group" role="group" aria-labelledby={labelId}>
      <h3 className="help-faq-group-label" id={labelId}>
        {group.label}
      </h3>
      {group.entries.map((entry) => (
        <FaqRow key={entry.id} entry={entry} open={openIds.has(entry.id)} onToggle={onToggle} />
      ))}
    </div>
  );
}

/**
 * One question and, when open, its answer.
 *
 * A real `<button>`, so Enter and Space are the browser's job — no key handling
 * of our own to get wrong (and none for `jsx-a11y` to object to). The panel is
 * unmounted when collapsed rather than hidden, which keeps collapsed answers out
 * of the accessibility tree entirely.
 */
function FaqRow({
  entry,
  open,
  onToggle,
}: {
  readonly entry: FaqEntry;
  readonly open: boolean;
  readonly onToggle: (id: string) => void;
}) {
  // Two ids from `useId` rather than one plus suffixes, matching ConfirmDialog:
  // the pair is symmetric — the trigger names the panel, the panel points back.
  const triggerId = useId();
  const panelId = useId();

  return (
    <div className="help-faq-row">
      <button
        type="button"
        className="help-faq-q"
        id={triggerId}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => {
          onToggle(entry.id);
        }}
      >
        <span>{entry.question}</span>
        {/* Rotation and the expanded Burnt Orange are driven off
            [aria-expanded='true'] in CSS, so `open` is stated in exactly one
            place — see TeamGroupHeader for the same technique. */}
        <ChevronDownIcon className="help-faq-chev" />
      </button>
      {open && (
        <div className="help-faq-a" role="region" id={panelId} aria-labelledby={triggerId}>
          {/* Index keys: answer blocks are authored, ordered copy that is never
              inserted into or reordered at runtime. */}
          {entry.answer.map((block, index) => (
            <FaqAnswer key={index} block={block} />
          ))}
        </div>
      )}
    </div>
  );
}

/** One answer block. All of `faq-content.ts`'s block markup lives here. */
function FaqAnswer({ block }: { readonly block: FaqAnswerBlock }) {
  switch (block.kind) {
    case 'paragraph':
      return (
        <p>
          <FaqInline spans={block.spans} />
        </p>
      );
    case 'hint':
      return (
        <p className="help-faq-hint">
          <FaqInline spans={block.spans} />
        </p>
      );
    case 'definitionList':
      return (
        <dl>
          {block.items.map((item) => (
            // Fragment, not a wrapper element: `dt`/`dd` have to stay direct
            // children of the `dl` for its two-column grid to line up.
            <Fragment key={item.term}>
              <dt>{item.term}</dt>
              <dd>{item.description}</dd>
            </Fragment>
          ))}
        </dl>
      );
  }
}

/** Inline runs: a bare string is prose, `{ strong }` is emphasis. */
function FaqInline({ spans }: { readonly spans: readonly FaqSpan[] }) {
  return (
    <>
      {spans.map((span, index) =>
        typeof span === 'string' ? (
          <Fragment key={index}>{span}</Fragment>
        ) : (
          // <strong>, not <b>: these are control names and status values the
          // reader has to act on, so the emphasis is meaning, not styling.
          <strong key={index}>{span.strong}</strong>
        ),
      )}
    </>
  );
}

/**
 * The Bluebonnet info callout — the empty state, and the degraded-role note.
 *
 * One component for both because they are the same object in the design: an
 * informational box that replaces or precedes the list.
 *
 * IT IS NEVER A LIVE REGION. It used to carry `role="status"` for the degraded
 * copy, which does not work: a live region mounted with its text already inside
 * is generally not announced, because the region has to exist for the change to
 * be a change. The degraded state announces through FaqDegradedAnnouncement
 * instead, and `PageFooterRows` documents the same constraint at length.
 */
function FaqCallout({
  title,
  body = 'Questions that come in through the paths below become the entries here. Everything else on this page works as normal.',
}: {
  readonly title: string;
  readonly body?: string;
}) {
  return (
    <div className="help-faq-callout">
      <InfoIcon className="help-faq-callout-icon" />
      <div>
        {/* Not a heading: it is a one-line lead-in to the sentence under it, and
            the section's h2 already names this content. A heading here would put
            an h3 in the tier-label slot for a state that has no tiers. */}
        <div className="help-faq-callout-title">{title}</div>
        <p>{body}</p>
      </div>
    </div>
  );
}

const DEGRADED_BODY =
  'Your role could not be loaded, so this list covers entering your own time. Everything else on this page works as normal.';

/**
 * Announces the degraded-role fallback, and nothing else.
 *
 * WHY THIS IS A SEPARATE COMPONENT FROM THE CALLOUT IT DESCRIBES. A live region is
 * announced when its CONTENT CHANGES, so one that mounts with its text already inside is
 * generally not announced at all — the region has to be present for there to be a change.
 * The visible callout is mounted by the state transition, so it can never be the region.
 *
 * This renders empty on its first commit and fills on the next, in an effect, which
 * guarantees a mutation even when `/v1/me` fails so fast that the degraded state is the
 * first thing painted. It is visually hidden because the callout beside it already says
 * the same thing on screen; duplicating it visibly would be noise.
 *
 * `PageFooterRows`'s copy confirmation solves the same problem and carries the longer
 * write-up. This is the second instance, so the pattern is worth recognising rather than
 * re-deriving: any message that arrives with its own container needs a container that
 * arrived earlier.
 */
function FaqDegradedAnnouncement() {
  const [announced, setAnnounced] = useState('');
  useEffect(() => {
    setAnnounced(`Showing the general answers. ${DEGRADED_BODY}`);
  }, []);
  return (
    <span className="sr-only" role="status" aria-live="polite">
      {announced}
    </span>
  );
}

/**
 * Announces that answers are loading.
 *
 * Mounted empty and filled one commit later, for the same reason the degraded note and the
 * copy confirmation are: a live region that already contains its text when it enters the DOM
 * is generally not announced. Lower stakes than the other two — the wrapper's `aria-busy`
 * already conveys the state and this text is a courtesy — but a third instance of the same
 * lifecycle is worth making consistent rather than leaving one of the three subtly different.
 * Flagged by Copilot on PR #74.
 */
function SkeletonAnnouncement() {
  const [announced, setAnnounced] = useState('');
  useEffect(() => {
    setAnnounced('Loading answers…');
  }, []);
  return (
    <span className="sr-only" role="status" aria-live="polite">
      {announced}
    </span>
  );
}

/**
 * The pending skeleton, shaped like the accordion (four 56px rows in the same
 * bordered container) so resolving `/v1/me` causes no layout jump.
 */
function FaqSkeleton() {
  return (
    <>
      {/* aria-busy on the skeleton wrapper, never on the live region below —
          per ARIA, aria-busy on a live region licenses AT to defer the
          announcement until it flips false, which here it never would. Same
          reasoning as Home.tsx's sign-in skeleton. */}
      <div className="help-faq help-faq--loading" aria-busy="true">
        {[0, 1, 2, 3].map((row) => (
          <div className="help-faq-row help-faq-skel-row" key={row}>
            <span className="skeleton help-faq-skel-bar" aria-hidden="true" />
          </div>
        ))}
      </div>
      <SkeletonAnnouncement />
    </>
  );
}
